import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/UI/Button/Button';
import Input from '../../components/UI/Input/Input';
import TextArea from '../../components/UI/TextArea/TextArea';
import './EditProfilePage.v2.css';
import Alert from '../../components/UI/Alert/Alert';
import { uploadFile } from '../../services/uploadService';
import { compressImage } from '../../utils/imageCompression';
import FileUploadField from '../../components/UI/FileUploadField/FileUploadField';

import ProfileView from '../../components/ProfileView/ProfileView';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const EditProfilePage = () => {
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const [formData, setFormData] = useState({
        artistName: '',
        location: '',
        studio: '',
        whatsapp: '',
        contactEmail: '',
        instagram: '',
        videoUrl: '',
        bio: '',
        portfolio: [], // will handle as array of strings
        specialtyStyles: [], // Use specialtyStyles to match backend model
        profilePicture: '',
        coverPhoto: '',
        backgroundSettings: { type: 'color', value: '#0a0a0a' }
    });
    const [availableStyles, setAvailableStyles] = useState([]);
    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info'
    });
    const [uploadingFields, setUploadingFields] = useState({});

    const closeAlert = () => {
        setAlertConfig(prev => ({ ...prev, isOpen: false }));
        if (alertConfig.onAfterClose) {
            alertConfig.onAfterClose();
        }
    };

    const showAlert = (title, message, type = 'info', onAfterClose = null) => {
        setAlertConfig({
            isOpen: true,
            title,
            message,
            type,
            onAfterClose
        });
    };

    const handleStyleToggle = (styleId) => {
        const currentStyles = formData.specialtyStyles || [];
        if (currentStyles.includes(styleId)) {
            setFormData({ ...formData, specialtyStyles: currentStyles.filter(id => id !== styleId) });
        } else {
            if (currentStyles.length >= 3) {
                showAlert(t('info'), t('max_styles_reached'), 'info');
                return;
            }
            setFormData({ ...formData, specialtyStyles: [...currentStyles, styleId] });
        }
    };

    useEffect(() => {
        const fetchStyles = async () => {
            try {
                const res = await axios.get(`${API_URL}/styles`);
                setAvailableStyles(res.data);
            } catch (err) {
                console.error("Error fetching styles", err);
            }
        };

        const fetchProfile = async () => {
            try {
                const res = await axios.get(`${API_URL}/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setFormData({
                        artistName: res.data.artistName || '',
                        location: res.data.location || '',
                        studio: res.data.studio || '',
                        whatsapp: res.data.whatsapp || '',
                        contactEmail: res.data.contactEmail || '',
                        instagram: res.data.instagram || '',
                        videoUrl: res.data.videoUrl || '',
                        bio: res.data.bio || '',
                        portfolio: res.data.portfolio || [],
                        specialtyStyles: res.data.specialtyStyles || [],
                        profilePicture: res.data.profilePicture || '',
                        coverPhoto: res.data.coverPhoto || '',
                        backgroundSettings: res.data.backgroundSettings || { type: 'color', value: '#0a0a0a' }
                    });
                }
            } catch (err) {
                console.error("Error fetching profile", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStyles();
        fetchProfile();
    }, [token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = async (e, field) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        setUploadingFields(prev => ({ ...prev, [field]: true }));
        try {
            if (field === 'portfolio') {
                const currentCount = formData.portfolio?.length || 0;
                const availableSlots = 6 - currentCount;

                if (availableSlots <= 0) {
                    showAlert(t('error'), t('gallery_limit_reached'), 'warning');
                    return;
                }

                const filesToUpload = files.slice(0, availableSlots);
                if (files.length > availableSlots) {
                    showAlert(t('info'), t('limit_reached_some_files_ignored', { count: availableSlots }), 'info');
                }

                for (const file of filesToUpload) {
                    try {
                        const compressed = await compressImage(file);
                        const url = await uploadFile(compressed, token, 'profiles');
                        setFormData(prev => ({
                            ...prev,
                            portfolio: [...(prev.portfolio || []), url]
                        }));
                    } catch (fileErr) {
                        console.error(`Error uploading portfolio file ${file.name}:`, fileErr);
                        showAlert(t('error'), `${file.name}: ${fileErr.message || t('artwork_error')}`, 'error');
                    }
                }
            } else {
                // Single file fields (profile picture, cover, background)
                const file = files[0];
                const compressed = await compressImage(file);
                const url = await uploadFile(compressed, token, 'profiles');

                if (field === 'background') {
                    setFormData({
                        ...formData,
                        backgroundSettings: { type: 'image', value: url }
                    });
                } else {
                    setFormData({ ...formData, [field]: url });
                }
            }
        } catch (err) {
            console.error(`Error uploading ${field}:`, err);
            showAlert(t('error'), err.message || t('artwork_error'), 'error');
        } finally {
            setUploadingFields(prev => ({ ...prev, [field]: false }));
        }
    };

    // Helper to get selected styles data for preview mapping
    const getSpecialtyStylesData = () => {
        return (formData.specialtyStyles || [])
            .map(id => availableStyles.find(s => s.id === id))
            .filter(Boolean);
    };


    const handleRemoveImage = (index) => {
        const newPortfolio = [...formData.portfolio];
        newPortfolio.splice(index, 1);
        setFormData({ ...formData, portfolio: newPortfolio });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API_URL}/profile`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showAlert(t('success'), t('profile_updated_success'), 'success', () => navigate(`/profile/${user?.username}`));
        } catch (err) {
            console.error(err);
            showAlert(t('error'), t('profile_update_error'), 'error');
        }
    };

    if (loading) return <div style={{ color: '#fff', padding: '100px', textAlign: 'center' }}>{t('loading')}</div>;

    return (
        <div className="edit-profile-container">
            {/* Navbar removed */}
            <div className="edit-profile-content">
                <h1 className="edit-profile-title">{t('edit_profile')}</h1>
                {user?.role === 'client' && (
                    <p className="section-subtitle" style={{ marginBottom: '20px', color: 'var(--color-text-secondary)' }}>
                        {t('client_edit_hint', 'Gerencie suas informações básicas de contato.')}
                    </p>
                )}

                <form onSubmit={handleSubmit} className="edit-profile-form">
                    <section className="form-section-card">
                        <h2 className="section-title">
                            <span className="section-icon">👤</span> {t('basic_info')}
                        </h2>
                        <div className="form-grid">
                            <Input
                                label={t('artist_name')}
                                name="artistName"
                                value={formData.artistName}
                                onChange={handleChange}
                                placeholder={t('artist_name_placeholder')}
                            />
                            <Input
                                label={t('location')}
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder={t('location_placeholder')}
                            />
                        </div>
                        <TextArea
                            label={t('bio')}
                            name="bio"
                            rows={4}
                            value={formData.bio}
                            onChange={handleChange}
                            placeholder={t('bio_placeholder')}
                        />
                    </section>

                    <section className="form-section-card">
                        <h2 className="section-title">
                            <span className="section-icon">📱</span> {t('contact_and_social')}
                        </h2>
                        <div className="form-grid">
                            <Input
                                label={t('whatsapp')}
                                name="whatsapp"
                                value={formData.whatsapp}
                                onChange={handleChange}
                                placeholder={t('whatsapp_placeholder')}
                            />
                            <Input
                                label={t('contact_email')}
                                type="email"
                                name="contactEmail"
                                value={formData.contactEmail}
                                onChange={handleChange}
                                placeholder={t('contact_email_placeholder')}
                            />
                            {user?.role !== 'client' && (
                                <Input
                                    label={t('studio')}
                                    name="studio"
                                    value={formData.studio}
                                    onChange={handleChange}
                                    placeholder={t('studio_placeholder')}
                                />
                            )}
                            <Input
                                label={t('instagram_label')}
                                name="instagram"
                                value={formData.instagram}
                                onChange={handleChange}
                                placeholder={t('instagram_placeholder')}
                            />
                        </div>
                        {user?.role !== 'client' && (
                            <Input
                                label={t('video_section_title')}
                                name="videoUrl"
                                value={formData.videoUrl}
                                onChange={handleChange}
                                placeholder={t('video_url_placeholder')}
                            />
                        )}
                    </section>

                    {user?.role !== 'client' && (
                        <>
                            <section className="form-section-card">
                                <h2 className="section-title">
                                    <span className="section-icon">🎨</span> {t('art_styles')}
                                </h2>
                                <p className="section-subtitle">{t('select_up_to_3')}</p>
                                <div className="styles-grid-modern">
                                    {availableStyles.map(style => (
                                        <button
                                            type="button"
                                            key={style.id}
                                            className={`style-chip-modern ${formData.specialtyStyles?.includes(style.id) ? 'active' : ''}`}
                                            onClick={() => handleStyleToggle(style.id)}
                                        >
                                            {t(`style_${style.name.toLowerCase().replace(/\s+/g, '_')}`) || style.name}
                                        </button>
                                    ))}
                                </div>
                            </section>

                            <section className="form-section-card">
                                <h2 className="section-title">
                                    <span className="section-icon">✨</span> {t('profile_appearance')}
                                </h2>

                                <div className="appearance-uploads">
                                    <FileUploadField
                                        label={t('profile_picture_url')}
                                        value={formData.profilePicture}
                                        uploading={uploadingFields.profilePicture}
                                        onChange={(e) => handleFileChange(e, 'profilePicture')}
                                        type="circle"
                                    />

                                    <FileUploadField
                                        label={t('cover_photo_url')}
                                        value={formData.coverPhoto}
                                        uploading={uploadingFields.coverPhoto}
                                        onChange={(e) => handleFileChange(e, 'coverPhoto')}
                                        type="rectangle"
                                    />
                                </div>

                                <div className="background-config">
                                    <div className="color-picker-group">
                                        <label className="field-label">{t('background_color')}</label>
                                        <div className="color-picker-wrapper">
                                            <input
                                                type="color"
                                                value={formData.backgroundSettings.type === 'color' ? formData.backgroundSettings.value : '#0a0a0a'}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    backgroundSettings: { type: 'color', value: e.target.value }
                                                })}
                                                className="color-input"
                                            />
                                            <span className="color-value">
                                                {formData.backgroundSettings.type === 'color' ? formData.backgroundSettings.value : t('custom_color')}
                                            </span>
                                        </div>
                                    </div>

                                    <FileUploadField
                                        label={t('background_image_url_optional')}
                                        value={formData.backgroundSettings.type === 'image' ? formData.backgroundSettings.value : ''}
                                        uploading={uploadingFields.background}
                                        onChange={(e) => handleFileChange(e, 'background')}
                                        type="rectangle"
                                    />
                                </div>
                            </section>

                            <section className="form-section-card">
                                <h2 className="section-title">
                                    <span className="section-icon">🖼️</span> {t('gallery_urls')}
                                </h2>
                                <p className="section-subtitle">{t('gallery_instructions')} • {t('max_6_images')}</p>

                                <div className="gallery-grid-modern">
                                    {(formData.portfolio?.length || 0) < 6 && (
                                        <FileUploadField
                                            uploading={uploadingFields.portfolio}
                                            onChange={(e) => handleFileChange(e, 'portfolio')}
                                            type="square"
                                            multiple={true}
                                            className="gallery-upload-item"
                                        />
                                    )}


                                    {formData.portfolio?.map((img, idx) => (
                                        <div key={idx} className="gallery-item-modern">
                                            <img src={img} alt="portfolio" className="gallery-image" />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(idx)}
                                                className="remove-image-btn-modern"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </>
                    )}

                    <div className="form-actions-sticky">
                        <Button type="submit" variant="primary" size="large" className="save-btn">
                            {t('save_profile')}
                        </Button>
                        {user?.role !== 'client' && (
                            <Button
                                type="button"
                                variant="outline"
                                size="large"
                                onClick={() => setShowPreview(!showPreview)}
                                className="preview-btn"
                            >
                                {showPreview ? t('close_preview') : t('preview_profile')}
                            </Button>
                        )}
                    </div>
                </form>

                <div className="public-link-card">
                    <h3>{t('your_public_link')}</h3>
                    <p style={{ color: '#aaa', margin: '10px 0' }}>{t('share_link_instructions')}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' }}>
                        <code className="public-link-code" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0, background: 'transparent' }}>
                            {window.location.origin}/profile/{user?.username}
                        </code>
                        <Button
                            type="button"
                            variant="primary"
                            size="small"
                            onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/profile/${user?.username}`);
                                showAlert(t('copied'), t('link_copied_msg'), 'success');
                            }}
                        >
                            {t('copy')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* 
                FAB moved to CSS class .preview-fab for better mobile handling 
                and to avoid overlap with bottom navigation.
            */}
            {
                user?.role !== 'client' && (
                    <button
                        onClick={() => setShowPreview(!showPreview)}
                        className={`preview-fab ${showPreview ? 'preview-active' : ''}`}
                        title={showPreview ? t('close_preview') : t('preview_profile')}
                    >
                        {showPreview ? '×' : '👁️'}
                    </button>
                )
            }

            {
                showPreview && (
                    <div className="preview-container" style={{
                        position: 'fixed',
                        zIndex: 2000,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: '#0a0a0a',
                        borderLeft: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '-5px 0 30px rgba(0,0,0,0.5)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease'
                    }}>
                        <button
                            className="close-preview-overlay-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowPreview(false);
                            }}
                            aria-label={t('close_preview')}
                        >
                            ×
                        </button>
                        <style>{`
                            .close-preview-overlay-btn {
                                position: fixed;
                                top: 20px;
                                right: 20px;
                                z-index: 2200;
                                width: 44px;
                                height: 44px;
                                border-radius: 50%;
                                background: rgba(0, 0, 0, 0.6);
                                color: white;
                                border: 1px solid rgba(255, 255, 255, 0.2);
                                font-size: 32px;
                                display: flex;
                                items-center: center;
                                justify-content: center;
                                cursor: pointer;
                                backdrop-filter: blur(10px);
                                -webkit-backdrop-filter: blur(10px);
                                line-height: 1;
                                padding-bottom: 4px; /* Visual alignment for X */
                            }

                            .preview-container {
                                position: fixed;
                                inset: 0;
                                overflow: hidden;
                                display: flex;
                                flex-direction: column;
                            }
                            
                            .preview-scaler {
                                flex: 1;
                                height: 100%;
                                width: 100%;
                                display: flex;
                                flex-direction: column;
                            }

                            .preview-content-scroll {
                                flex: 1;
                                width: 100%;
                                height: 100%;
                                overflow-y: auto !important;
                                overflow-x: hidden;
                                background: #0a0a0a;
                                -webkit-overflow-scrolling: touch;
                                position: relative;
                            }

                            @media (min-width: 1024px) {
                                .close-preview-overlay-btn {
                                    display: none; /* Already have the FAB on desktop */
                                }
                                .preview-container {
                                    top: auto;
                                    left: auto;
                                    bottom: 100px;
                                    right: 30px;
                                    width: 640px;
                                    height: 360px;
                                    border-radius: 12px;
                                    border: 1px solid #444;
                                    box-shadow: 0 20px 50px rgba(0,0,0,0.8);
                                    transform-origin: bottom right;
                                    display: block;
                                    background: #000;
                                }
                                
                                .preview-scaler {
                                    width: 1280px; 
                                    height: 720px;
                                    transform: scale(0.5);
                                    transform-origin: top left;
                                }
                                
                                .preview-content-scroll {
                                    /* This creates a new containing block for fixed position children */
                                    transform: translateZ(0); 
                                    -webkit-transform: translateZ(0);
                                }
                                
                                .preview-content-scroll::-webkit-scrollbar {
                                    width: 12px;
                                }
                                .preview-content-scroll::-webkit-scrollbar-track {
                                    background: #1a1a1a;
                                }
                                .preview-content-scroll::-webkit-scrollbar-thumb {
                                    background: #444;
                                    border-radius: 6px;
                                    border: 2px solid #1a1a1a;
                                }
                            }
                        `}</style>
                        <div className="preview-scaler">
                            <div className="preview-content-scroll">
                                <ProfileView
                                    data={{
                                        ...formData,
                                        specialtyStylesData: getSpecialtyStylesData()
                                    }}
                                    username={user?.username}
                                    isPreview={true}
                                    onRequestQuote={() => console.log('Preview: Request Quote clicked')}
                                />
                            </div>
                        </div>
                    </div>
                )
            }

            <Alert
                isOpen={alertConfig.isOpen}
                title={alertConfig.title}
                message={alertConfig.message}
                type={alertConfig.type}
                onClose={closeAlert}
            />
        </div >
    );
};

export default EditProfilePage;
